import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../api/client';
import type { Facility } from '../api/types';
import { useApp } from '../state/AppContext';
import { EmptyRow, ErrorBanner, SortableHeader, useTableSort } from '../components/ui';

type Draft = { facilityId: number; code: string; name: string; isActive: boolean };

/**
 * The locations every Cyntox app shares. Hand-maintained: a short list, not a
 * bulk feed. Employee imports validate Location against these codes, so
 * without a way to add one here a new facility from the HR export would
 * reject every employee at it with no way to fix it short of SQL. Access by
 * location hangs off the same codes.
 *
 * The code itself is not editable. Employees and user roles reference it by
 * value with no foreign key to enforce it, so a rename would silently orphan
 * both.
 */
export function Facilities() {
  const { can } = useApp();
  const queryClient = useQueryClient();
  const readOnly = !can('reference', 'edit');

  // The full list. reference.facilities is narrowed to the viewer's own
  // locations for pickers, which would hide the rest of the company here.
  const list = useQuery({
    queryKey: ['facilities-all'],
    queryFn: () => api.get<Facility[]>('/api/facilities'),
  });
  const { rows, sort, onSort } = useTableSort(list.data ?? [], {
    code: (f) => f.code,
    name: (f) => f.name,
  });

  const [draft, setDraft] = useState<Draft | null>(null);
  const [error, setError] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: (v: Draft) =>
      v.facilityId > 0
        ? api.put(`/api/facilities/${v.facilityId}`, v)
        : api.post('/api/facilities', v),
    onSuccess: () => { setDraft(null); void queryClient.invalidateQueries(); },
    onError: (e: Error) => setError(e.message),
  });

  const blank = (): Draft => ({ facilityId: 0, code: '', name: '', isActive: true });
  const commit = () => {
    if (!draft) return;
    setError(null);
    save.mutate({
      facilityId: draft.facilityId,
      code: draft.code.trim(),
      name: draft.name.trim(),
      isActive: draft.isActive,
    });
  };

  return (
    <div>
      <div className="page-header">
        <div>
          <div className="page-title">Facilities</div>
          <div className="page-subtitle">The locations every Cyntox app shares</div>
        </div>
        {!readOnly && !draft && (
          <button className="btn" onClick={() => setDraft(blank())}>Add facility</button>
        )}
      </div>

      {error && <ErrorBanner message={error} onDismiss={() => setError(null)} />}

      <div className="meta" style={{ marginBottom: 10 }}>
        Hand-maintained here rather than imported — a short list, not a bulk feed. An employee's location must
        match one of these codes or the Employees import rejects the row, so a facility appearing in the HR export
        needs adding here first. The code cannot be changed afterwards: employees and location-scoped users
        reference it by value, and renaming would orphan them.
      </div>

      {draft && (
        <div className="card" style={{ marginBottom: 12 }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12, marginBottom: 12 }}>
            <label>
              <div className="field-label">CODE</div>
              <input
                className="control"
                value={draft.code}
                disabled={draft.facilityId > 0}
                placeholder="Indy-Midwest"
                onChange={(e) => setDraft({ ...draft, code: e.target.value })}
              />
              {draft.facilityId > 0 && <div className="meta" style={{ marginTop: 4 }}>Fixed once created.</div>}
            </label>
            <label style={{ gridColumn: 'span 2' }}>
              <div className="field-label">NAME</div>
              <input className="control" value={draft.name} placeholder="Indianapolis Midwest"
                onChange={(e) => setDraft({ ...draft, name: e.target.value })} />
            </label>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 20, marginBottom: 12 }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
              <input type="checkbox" checked={draft.isActive}
                onChange={(e) => setDraft({ ...draft, isActive: e.target.checked })} />
              Active
            </label>
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
            <button className="btn" onClick={() => setDraft(null)}>Cancel</button>
            <button
              className="btn btn--primary"
              disabled={!draft.code.trim() || !draft.name.trim() || save.isPending}
              onClick={commit}
            >
              Save
            </button>
          </div>
        </div>
      )}

      <div className="table-wrap">
        <table className="table">
          <thead>
            <tr>
              <SortableHeader label="CODE" column="code" sort={sort} onSort={onSort} />
              <SortableHeader label="NAME" column="name" sort={sort} onSort={onSort} />
              {!readOnly && <th />}
            </tr>
          </thead>
          <tbody>
            {list.isLoading && <EmptyRow colSpan={readOnly ? 2 : 3}>Loading…</EmptyRow>}
            {list.isError && <EmptyRow colSpan={readOnly ? 2 : 3}>{(list.error as Error).message}</EmptyRow>}
            {rows.map((f) => (
              <tr key={f.facilityId} style={f.isActive ? undefined : { opacity: 0.55 }}>
                <td className="strong">{f.code}</td>
                <td>{f.name}{f.isActive ? '' : ' · inactive'}</td>
                {!readOnly && (
                  <td className="num">
                    <button
                      className="btn btn--sm"
                      onClick={() => setDraft({
                        facilityId: f.facilityId, code: f.code, name: f.name, isActive: f.isActive,
                      })}
                    >
                      Edit
                    </button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
